<template>
    <div class="card border-0 bg-white profile-settings-card rounded-0">
        <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
            <form v-on:submit="submit">
                <div class="row">
                    <div class="col-12">
                        <p class="fs-md-15 fs-lg-16 text-paragraph sub-text">
                            {{ $t('feature.auth.$request_support.message') }}
                        </p>
                    </div>
                    <!-- EMAIL -->
                    <div class="col-md-6">
                        <div class="form-group mb-15 mb-sm-20">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('form.email.label') }}
                            </label>
                            <input type="email" :title="$t('form.email.label')"
                                class="form-control shadow-none text-black" v-model="form.email" disabled
                                :class="[{ 'is-invalid': submitted && !email_is_valid(form.email) && errors?.email }]"
                                :placeholder="$t('form.email.placeholder')" required
                                pattern="[^@\s]+@[^@\s]+\.[^@\s]+" />
                        </div>
                        <!-- ERRORS -->
                        <div class="form-messages mt-2 px-1" v-if="errors?.email ?? false">
                            <p v-for="error in errors.email" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                                error }}</p>
                        </div>
                    </div>
                    <div class="col-12"></div>
                    <!-- FIRST NAME -->
                    <div class="col-md-6">
                        <div class="form-group mb-15 mb-sm-20">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('form.first_name.label') }}
                            </label>
                            <input :title="$t('form.first_name.label')" class="form-control shadow-none text-black"
                                disabled v-model="form.first_name"
                                :class="[{ 'is-invalid': submitted && !form.first_name && errors?.first_name }]"
                                :placeholder="$t('form.first_name.placeholder')" required />
                        </div>
                        <!-- ERRORS -->
                        <div class="form-messages mt-2 px-1" v-if="errors?.first_name ?? false">
                            <p v-for="error in errors.first_name" :key="error" class="small text-danger lh-1 m-0 mt-1">
                                {{
                                    error }}</p>
                        </div>
                    </div>
                    <!-- LAST NAME -->
                    <div class="col-md-6">
                        <div class="form-group mb-15 mb-sm-20">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('form.last_name.label') }}
                            </label>
                            <input :title="$t('form.last_name.label')" class="form-control shadow-none text-black"
                                disabled v-model="form.last_name"
                                :class="[{ 'is-invalid': submitted && !form.last_name && errors?.last_name }]"
                                :placeholder="$t('form.last_name.placeholder')" required />
                        </div>
                        <!-- ERRORS -->
                        <div class="form-messages mt-2 px-1" v-if="errors?.last_name ?? false">
                            <p v-for="error in errors.last_name" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                                error }}</p>
                        </div>
                    </div>
                    <!-- REQUEST TYPE -->
                    <div class="col-md-6">
                        <div class="form-group mb-15 mb-sm-20">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('feature.auth.$request_support.request_type') }}
                            </label>
                            <select class="form-select shadow-none text-black fs-md-15" v-model="form.request_type"
                                required
                                :class="[{ 'is-invalid': submitted && (!form.request_type || form.request_type === -1) && errors?.request_type }]">
                                <option value="" selected disabled>{{ $t('form.select_placeholder') }}</option>
                                <template v-if="labels">
                                    <option v-for="label in labels" :value="label.id" :key="label.id">{{
                                        label.description }}</option>
                                </template>
                            </select>
                            <!-- ERRORS -->
                            <div class="form-messages mt-2 px-1" v-if="errors?.request_type ?? false">
                                <p v-for="error in errors.request_type" :key="error"
                                    class="small text-danger lh-1 m-0 mt-1">{{
                                        error }}</p>
                            </div>
                        </div>
                    </div>
                    <div class="col-12"></div>
                    <!-- MESSAGE -->
                    <div class="col-md-6">
                        <div class="form-group mb-15 mb-sm-20">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('form.message.label') }}
                            </label>
                            <textarea :title="$t('form.message.label')" class="form-control shadow-none text-black"
                                v-model="form.message"
                                :class="[{ 'is-invalid': submitted && !form.message && errors?.message }]"
                                :placeholder="$t('form.message.placeholder')" required rows="6"></textarea>
                        </div>
                        <!-- ERRORS -->
                        <div class="form-messages mt-2 px-1" v-if="errors?.message ?? false">
                            <p v-for="error in errors.message" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                                error }}</p>
                        </div>
                    </div>
                </div>
                <div class="col-md-12">
                    <div class="d-flex align-items-center justify-content-end" v-if="!loading">
                        <!-- SAVE -->
                        <div class="d-flex flex-wrap gap-3 justify-content-end ms-auto">
                            <DiscardButton @confirm="$router.push({ path: '/profile' })" />
                            <button
                                class="default-btn transition fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 fs-md-15 fs-lg-16"
                                type="submit" v-on:click="setPristine">
                                {{ $t('cta.submit') }}
                            </button>
                        </div>
                    </div>
                    <SpinnerLoading v-else />
                </div>
            </form>
        </div>
    </div>
    <!-- ERROR MESSAGE -->
    <div class="row justify-content-center">
        <div class="col">
            <div class="alert alert-danger alert-dismissible fade border-0 border-danger-subtle border-top rounded-0 m-0 mb-25"
                role="alert" id="errorMessage">
                <strong>{{ $t('error.something_went_wrong') }}</strong>
                <button type="button" class="btn-close" v-on:click="hideError" aria-label="Close"></button>
            </div>
        </div>
    </div>

    <AlertModal id="alertModal" :title="$t('feature.auth.request_support')" :dismissible="false"
        :message="$t('feature.auth.$request_support.success_message')" @confirm="redirect" />
</template>

<script lang="ts">
import { defineComponent } from "vue";
import AlertModal from "@/components/Widgets/AlertModal.vue";
import API from "@/services/api/api.service";
import { Alert, Modal } from "bootstrap";
import stateStore from "@/utils/store";
import SpinnerLoading from "@/components/Widgets/Spinner.vue";
import DiscardButton from "@/components/Widgets/DiscardButton.vue";
import { email_is_valid, request_support_form, RequestSupportForm } from "@/utils/methods/form_utils";
import { getInstance, ReCaptchaInstance } from "recaptcha-v3";
import { Label } from "@/utils/models/label.model";

export default defineComponent({
    name: "RequestSupportForm",
    components: {
        AlertModal,
        SpinnerLoading,
        DiscardButton
    },
    data: function () {
        return {
            form: { ...request_support_form, email: stateStore.user?.info.email, first_name: stateStore.user?.info.firstname, last_name: stateStore.user?.info.lastname } as RequestSupportForm,
            submitted: false,
            errorMessage: undefined as Alert | undefined,
            errors: undefined as any,
            alertModal: null as Modal | null,
            loading: false,
            request_types: [1, 2, 3] as number[],
            recaptcha: undefined as ReCaptchaInstance | undefined,
            labels: undefined as Label[] | undefined,
        }
    },
    watch: {
        '$i18n.locale': function (oldVal, newVal) {
            this.getRequestTypes();
        }
    },
    async mounted() {
        this.errorMessage = new Alert('#errorMessage');
        this.alertModal = new Modal('#alertModal', {});
        this.recaptcha = await getInstance();
        this.recaptcha?.hideBadge();
        this.getRequestTypes();
    },
    beforeUnmount() {
        this.recaptcha?.hideBadge();
    },
    methods: {
        submit(event): void {
            this.submitted = true;
            this.errors = undefined;
            event.preventDefault();
            console.debug("SUBMIT");
            this.sendRequest();
        },
        async sendRequest() {
            this.loading = true;
            this.form['g-recaptcha-response'] = await this.recaptcha?.execute('support_request');
            API.getSupport(this.form).then((response) => {
                console.debug(response);
                if (response.data.success === true) {
                    this.errors = undefined;
                    this.form = { ...request_support_form, username: stateStore.user?.info.email, first_name: stateStore.user?.info.firstname, last_name: stateStore.user?.info.lastname } as RequestSupportForm;
                    this.alertModal?.show();
                }
            }).catch((error) => {
                this.errors = error.response?.data?.errors;
                this.showError();
            }).finally(() => {
                this.loading = false;
            })
        },
        email_is_valid(email?: string) {
            return email_is_valid(email);
        },
        setPristine() {
            this.submitted = true;
        },
        showError() {
            const error = document.getElementById('errorMessage');
            error?.classList.add('show');
            setTimeout(() => {
                this.hideError();
            }, 5000)
        },
        hideError() {
            const error = document.getElementById('errorMessage');
            error?.classList.remove('show');
        },
        redirect() {
            this.$router.push('/');
        },
        getRequestTypes() {
            API.getLabels({ label: 'dashboard_supports', lang: this.$i18n.locale }).then((response) => {
                if (response.data.success === true) {
                    this.labels = response.data.data;
                }
            });
        },
    },
});
</script>